import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import Collapse from "@mui/material/Collapse";
import {styled} from "@mui/material/styles";
import {DatePicker} from "@mui/x-date-pickers";
import {connect} from "formik";
import React, {useContext, useEffect, useState} from "react";
import {SnackContext} from "../App";
import axios from "../utils/axios";
import dayjs from "dayjs";
import {MuiTelInput} from "mui-tel-input";

const Input = styled("input")({
    display: "none",
});

const Step1 = ({formik, countries, destinations: destinations, onLocationSelected}) => {
    const {setIsSnackOpen} = useContext(SnackContext);

    const [locations, setLocations] = useState(null);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [warningOpen, setWarningOpen] = useState(true);
    const [arrivalMax, setArrivalMax] = useState();
    const [departMin, setDepartMin] = useState();

    const fetchLocations = (id) => {
        axios
            .get(`country/${id}/location`)
            .then(function (response) {
                if (!response.data) return;

                setLocations(response.data.data);
            })
            .catch(function (error) {
                setIsSnackOpen(true);
            });
    };

    useEffect(() => {
        window.scrollTo({top: 500, left: 0, behavior: "smooth"});
    }, []);

    useEffect(() => {
        if (!locations && formik.values.destination) {
            fetchLocations(formik.values.destination);
        }

        if (formik.values.id_or_passport) {
            var reader = new FileReader();

            reader.onload = function (e) {
                setPreview(e.target.result);
            };

            reader.readAsDataURL(formik.values.id_or_passport);
        }

        if (!formik.touched.departure_date) {
            setArrivalMax(null);
        }
    }, [formik.values]);

    return (
        <>
            <Box mb={2}>
                <Collapse in={warningOpen}>
                    <Alert
                        severity="warning"
                        action={
                            <IconButton
                                aria-label="close"
                                color="inherit"
                                size="small"
                                onClick={() => {
                                    setWarningOpen(false);
                                }}
                            >
                                <CloseIcon fontSize="inherit"/>
                            </IconButton>
                        }
                    >
                        "Note: All fields marked with a cross (*) are mandatory."
                    </Alert>
                </Collapse>
            </Box>

            <Box display="flex" j alignItems="center" mb={1}>
                <Typography variant="h5" mr={1}>
                    Basic data
                </Typography>

                {/* {!warningOpen && (
          <IconButton
            aria-label="close"
            size="small"
            onClick={() => {
              setWarningOpen(true);
            }}
          >
            <InfoOutlinedIcon fontSize="inherit" color="#757575" />
          </IconButton>
        )} */}
            </Box>
            <Box mb={3}>
                <FormControl fullWidth>
                    <InputLabel id="destination-select-label" required>
                        Destination
                    </InputLabel>
                    <Select
                        name="destination"
                        labelId="destination-select-label"
                        id="destination-select"
                        value={formik.values.destination}
                        onChange={(e) => {
                            const countryId = e.target.value;

                            fetchLocations(countryId);

                            formik.setFieldValue("destination", countryId, true);
                        }}
                        error={
                            formik.touched.destination && Boolean(formik.errors.destination)
                        }
                        required
                        disabled={!destinations}
                    >
                        {(destinations || []).map((o) => (
                            <MenuItem value={o._id} key={o._id}>
                                {o.title}
                            </MenuItem>
                        ))}
                    </Select>

                    {formik.touched.destination && Boolean(formik.errors.destination) && (
                        <FormHelperText error>{formik.errors.destination}</FormHelperText>
                    )}
                </FormControl>
            </Box>

            <Box mb={3}>
                <FormControl fullWidth>
                    <InputLabel id="location-select-label" required>
                        Select your holiday
                    </InputLabel>
                    <Select
                        name="location"
                        labelId="location-select-label"
                        id="location-select"
                        value={formik.values.location}
                        onChange={(e) => {
                            const locationId = e.target.value;

                            let rooms = [];
                            let addons = [];
                            const selectedLocation = locations.find(
                                (loc) => loc._id === locationId
                            );

                            if (selectedLocation) {
                                rooms = selectedLocation.rooms;
                                addons = selectedLocation.addons;

                                onLocationSelected(selectedLocation);
                            }

                            axios
                                .get(`location/${locationId}/rooms`)
                                .then(function (response) {
                                    if (!response.data) return;
                                })
                                .catch(function (error) {
                                    setIsSnackOpen(true);
                                });

                            formik.setFieldValue("rooms", rooms, true);
                            formik.setFieldValue("addons", addons, true);
                            formik.setFieldValue("location", locationId, true);
                        }}
                        error={formik.touched.location && Boolean(formik.errors.location)}
                        required
                        disabled={!locations}
                    >
                        {(locations || []).map((o) => (
                            <MenuItem value={o._id} key={o._id}>
                                {o.title}
                            </MenuItem>
                        ))}
                    </Select>

                    {formik.touched.location && Boolean(formik.errors.location) && (
                        <FormHelperText error>{formik.errors.location}</FormHelperText>
                    )}
                </FormControl>
            </Box>

            <Grid container mb={3} spacing={3}>
                <Grid item xs={6}>
                    <DatePicker
                        name="arrival_date"
                        value={formik.values.arrival_date}
                        disablePast
                        maxDate={arrivalMax}
                        onChange={(value) => {
                            setDepartMin(dayjs(value).add(1, "day"));
                            formik.setFieldValue("arrival_date", value, true);
                        }}
                        error={
                            formik.touched.arrival_date && Boolean(formik.errors.arrival_date)
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth
                                error={
                                    formik.touched.arrival_date &&
                                    Boolean(formik.errors.arrival_date)
                                }
                                helperText={
                                    formik.touched.arrival_date && formik.errors.arrival_date
                                }
                            />
                        )}
                        required
                    />
                </Grid>
                <Grid item xs={6}>
                    <DatePicker
                        name="departure_date"
                        value={formik.values.departure_date}
                        disablePast
                        minDate={departMin}
                        onChange={(value) => {
                            setArrivalMax(dayjs(value).subtract(1, "day"));
                            formik.setFieldValue("departure_date", value, true);
                        }}
                        error={
                            formik.touched.departure_date &&
                            Boolean(formik.errors.departure_date)
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth
                                error={
                                    formik.touched.departure_date &&
                                    Boolean(formik.errors.departure_date)
                                }
                                helperText={
                                    formik.touched.departure_date && formik.errors.departure_date
                                }
                            />
                        )}
                        required
                    />
                </Grid>
            </Grid>

            <Box mt={10}/>

            <Box display="flex" j alignItems="center" mb={1}>
                <Typography variant="h5" mr={1}>
                    Booker details
                </Typography>

                {/* {!warningOpen && (
          <IconButton
            aria-label="close"
            size="small"
            onClick={() => {
              setWarningOpen(true);
            }}
          >
            <InfoOutlinedIcon fontSize="inherit" color="#757575" />
          </IconButton>
        )} */}
            </Box>

            <Grid container mb={3} spacing={3}>
                <Grid item xs={6}>
                    <TextField
                        fullWidth
                        id="first_name"
                        name="first_name"
                        label="First name"
                        type="text"
                        value={formik.values.first_name}
                        onChange={formik.handleChange}
                        error={
                            formik.touched.first_name && Boolean(formik.errors.first_name)
                        }
                        helperText={formik.touched.first_name && formik.errors.first_name}
                        required
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        fullWidth
                        id="last_name"
                        name="last_name"
                        label="Last name"
                        type="text"
                        value={formik.values.last_name}
                        onChange={formik.handleChange}
                        error={formik.touched.last_name && Boolean(formik.errors.last_name)}
                        helperText={formik.touched.last_name && formik.errors.last_name}
                        required
                    />
                </Grid>
            </Grid>
            <Box mb={3}>
                <FormControl fullWidth>
                    <TextField
                        fullWidth
                        id="email"
                        name="email"
                        label="Email"
                        type="email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        error={formik.touched.email && Boolean(formik.errors.email)}
                        helperText={formik.touched.email && formik.errors.email}
                        required
                    />
                </FormControl>
            </Box>
            <Box mb={3}>
                <FormControl fullWidth>
                    <TextField
                        fullWidth
                        id="address"
                        name="address"
                        label="Address"
                        type="text"
                        value={formik.values.address}
                        onChange={formik.handleChange}
                        error={formik.touched.address && Boolean(formik.errors.address)}
                        helperText={formik.touched.address && formik.errors.address}
                        required
                    />
                </FormControl>
            </Box>
            <Box mb={3}>
                <FormControl fullWidth>
                    <TextField
                        fullWidth
                        id="city"
                        name="city"
                        label="City"
                        type="text"
                        value={formik.values.city}
                        onChange={formik.handleChange}
                        error={formik.touched.city && Boolean(formik.errors.city)}
                        helperText={formik.touched.city && formik.errors.city}
                        required
                    />
                </FormControl>
            </Box>
            <Box mb={3}>
                <Autocomplete
                    name="country"
                    value={formik.values.country}
                    onChange={(e, value) => {
                        formik.setFieldValue("country", value.label, true);
                    }}
                    options={countries}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            error={formik.touched.country && Boolean(formik.errors.country)}
                            helperText={formik.touched.country && formik.errors.country}
                            label="Country"
                            required
                            inputProps={{
                                ...params.inputProps,
                                autoComplete: "new-password",
                            }}
                        />
                    )}
                    required
                />
            </Box>
            <Box mb={3}>
                <FormControl fullWidth>
                    <MuiTelInput
                        fullWidth
                        forceCallingCode
                        id="phone"
                        name="phone"
                        label="Phone"
                        value={formik.values.phone}
                        onChange={(value) => {
                            formik.setFieldValue("phone", value);
                        }}
                        error={
                            formik.touched.phone &&
                            Boolean(formik.errors.phone)
                        }
                        helperText={
                            formik.touched.phone && formik.errors.phone
                        }
                        required
                    />
                </FormControl>
            </Box>
            <FormControl fullWidth>
                <Box display="flex" justifyContent="space-between" position="relative">
                    <Box>
                        <Typography variant="body1" mb={1}>
                            Copy of Passport or I.D *
                        </Typography>
                        <label htmlFor="contained-button-file">
                            <Input
                                accept="image/*"
                                id="contained-button-file"
                                name="id_or_passport"
                                multiple
                                type="file"
                                onChange={(event) => {
                                    const f = event.target.files[0];
                                    setFile(f);

                                    formik.setFieldValue("id_or_passport", f, true);

                                    var reader = new FileReader();

                                    reader.onload = function (e) {
                                        setPreview(e.target.result);
                                    };

                                    reader.readAsDataURL(event.target.files[0]);
                                }}
                            />
                            <Button variant="contained" component="span">
                                Upload
                            </Button>
                        </label>

                        {formik.touched.id_or_passport &&
                            Boolean(formik.errors.id_or_passport) && (
                                <FormHelperText error>
                                    {formik.errors.id_or_passport}
                                </FormHelperText>
                            )}
                    </Box>

                    {(preview || file) && (
                        <Box
                            display="flex"
                            flexDirection="column"
                            style={{maxWidth: 300}}
                        >
                            <IconButton
                                aria-label="delete"
                                color="primary"
                                size="small"
                                style={{
                                    position: "absolute",
                                    top: 8,
                                    right: 8,
                                    background: "white",
                                }}
                                onClick={() => {
                                    formik.setFieldValue("id_or_passport", null, true);
                                    setPreview(null);
                                    setFile(null);
                                }}
                            >
                                <DeleteIcon/>
                            </IconButton>

                            {preview && (
                                <img
                                    src={preview}
                                    style={{maxWidth: 300, borderRadius: 4}}
                                    alt="Uploaded ID or passport"
                                />
                            )}

                            {file && <Typography variant="caption">{file.name}</Typography>}
                        </Box>
                    )}
                </Box>
                <Box mt={10}/>
            </FormControl>
            <Box display="flex" j alignItems="center" mb={1}>
                <Typography variant="h5" mr={1}>
                    Emergency contact
                </Typography>

                {/* {!warningOpen && (
          <IconButton
            aria-label="close"
            size="small"
            onClick={() => {
              setWarningOpen(true);
            }}
          >
            <InfoOutlinedIcon fontSize="inherit" color="#757575" />
          </IconButton>
        )} */}
            </Box>
            <Box mb={3}>
                <FormControl fullWidth>
                    <TextField
                        fullWidth
                        id="emergency_name"
                        name="emergency_name"
                        label="Emergency contact name"
                        type="name"
                        value={formik.values.emergency_name}
                        onChange={formik.handleChange}
                        error={
                            formik.touched.emergency_name &&
                            Boolean(formik.errors.emergency_name)
                        }
                        helperText={
                            formik.touched.emergency_name && formik.errors.emergency_name
                        }
                        required
                    />
                </FormControl>
            </Box>
            <Box mb={3}>
                <FormControl fullWidth>
                    <TextField
                        fullWidth
                        id="emergency_email"
                        name="emergency_email"
                        label="Emergency Email"
                        type="email"
                        value={formik.values.emergency_email}
                        onChange={formik.handleChange}
                        error={
                            formik.touched.emergency_email &&
                            Boolean(formik.errors.emergency_email)
                        }
                        helperText={
                            formik.touched.emergency_email && formik.errors.emergency_email
                        }
                        required
                    />
                </FormControl>
            </Box>
            <Box mb={3}>
                <FormControl fullWidth>
                    <MuiTelInput
                        forceCallingCode
                        fullWidth
                        id="emergency_phone"
                        name="emergency_phone"
                        label="Emergency Phone"
                        type="phone"
                        value={formik.values.emergency_phone}
                        onChange={(value) => {
                            formik.setFieldValue("emergency_phone", value);
                        }}
                        error={
                            formik.touched.emergency_phone &&
                            Boolean(formik.errors.emergency_phone)
                        }
                        helperText={
                            formik.touched.emergency_phone && formik.errors.emergency_phone
                        }
                        required
                    />
                </FormControl>
            </Box>
        </>
    );
};

export default connect(Step1);
